/**
 * Sources Table Component
 * Displays health metrics for all sources with crawl trigger functionality
 */

import type { SourceHealth } from '@tender-monitor/shared/types/admin';
import { formatTimestamp } from '../../utils/formatTime';

type SourcesTableProps = {
  items: SourceHealth[];
  loading: boolean;
  crawlingSourceIds: Set<string>;
  togglingSourceIds: Set<string>;
  onCrawl: (sourceId: string, sourceName: string) => void;
  onToggle: (sourceId: string, isActive: boolean) => void;
};

function Timestamp({ value }: { value: string | null }) {
  const { relative, absolute } = formatTimestamp(value, 'Nigdy');
  return (
    <span className="timestamp" title={absolute}>
      {relative}
    </span>
  );
}

function ActiveBadge({ isActive, errorCount }: { isActive: boolean; errorCount: number }) {
  if (!isActive) {
    return (
      <span className="status-badge status-badge--failed" title="Auto-deaktywowane po 5 błędach">
        Nieaktywne
      </span>
    );
  }
  if (errorCount > 0) {
    return (
      <span className="status-badge status-badge--pending" title={`${errorCount} błędów`}>
        Aktywne ({errorCount} błędów)
      </span>
    );
  }
  return <span className="status-badge status-badge--success">Aktywne</span>;
}

export function SourcesTable({ items, loading, crawlingSourceIds, togglingSourceIds, onCrawl, onToggle }: SourcesTableProps) {
  if (items.length === 0) {
    return (
      <div className="empty-state">
        <p>Brak źródeł</p>
      </div>
    );
  }

  return (
    <div>
      <table className="admin-table">
        <thead>
          <tr>
            <th>Status</th>
            <th>Nazwa</th>
            <th>Województwo</th>
            <th>Linki</th>
            <th>Sukces / Błąd</th>
            <th>Ostatnie pobranie</th>
            <th>Akcje</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const isCrawling = crawlingSourceIds.has(item.id);
            const isToggling = togglingSourceIds.has(item.id);
            return (
              <tr key={item.id} style={{ opacity: loading ? 0.5 : 1 }}>
                <td>
                  <ActiveBadge isActive={item.isActive} errorCount={item.errorCount} />
                </td>
                <td>{item.name}</td>
                <td style={{ textTransform: 'capitalize' }}>{item.voivodeship}</td>
                <td>{item.totalLinks}</td>
                <td>
                  <span style={{ color: '#276749' }}>{item.completedLinks}</span>
                  {' / '}
                  <span style={{ color: item.failedLinks > 0 ? '#c53030' : '#718096' }}>
                    {item.failedLinks}
                  </span>
                </td>
                <td>
                  <Timestamp value={item.lastCrawledAt} />
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className={`admin-button admin-button--small ${isToggling ? 'admin-button--loading' : ''} ${item.isActive ? 'admin-button--danger' : 'admin-button--success'}`}
                      onClick={() => onToggle(item.id, !item.isActive)}
                      disabled={isToggling}
                      title={item.isActive ? 'Wyłącz źródło' : 'Włącz źródło'}
                    >
                      {isToggling ? '...' : item.isActive ? 'Wyłącz' : 'Włącz'}
                    </button>
                    <button
                      className={`admin-button admin-button--small ${isCrawling ? 'admin-button--loading' : ''}`}
                      onClick={() => onCrawl(item.id, item.name)}
                      disabled={isCrawling || !item.isActive}
                      title={!item.isActive ? 'Źródło nieaktywne' : isCrawling ? 'Pobieranie w toku...' : 'Uruchom pobieranie'}
                    >
                      {isCrawling ? '...' : 'Pobierz'}
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#718096' }}>
        Sortowanie: najpierw źródła z błędami
      </div>
    </div>
  );
}
